/**
 * Messaging design-system primitives.
 *
 * Every messaging surface composes these — so avatars, badges, chips, empty
 * states, loading states and error states look and behave identically for
 * applicants, employers, referrers, agents and admins.
 */

import React from 'react';
import {
  AlertTriangle,
  ChevronDown,
  MessageSquare,
  RefreshCw,
  WifiOff,
  X,
} from 'lucide-react';
import './messaging.css';
import { formatDateSeparator, getInitials } from './utils';

/* ─── Avatar ──────────────────────────────────────────────────────────────── */

export interface MessagingAvatarProps {
  name?: string;
  photoUrl?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  online?: boolean;
  muted?: boolean;
  /** Renders an icon instead of initials (used for system/support rows). */
  icon?: React.ReactNode;
  className?: string;
}

export const MessagingAvatar = React.memo(function MessagingAvatar({
  name,
  photoUrl,
  size = 'md',
  online,
  muted,
  icon,
  className = '',
}: MessagingAvatarProps) {
  const [failed, setFailed] = React.useState(false);
  const sizeClass = size === 'md' ? '' : `msg-avatar--${size}`;
  const showImage = !!photoUrl && !failed;

  return (
    <span
      className={`msg-avatar ${sizeClass} ${muted ? 'msg-avatar--muted' : ''} ${className}`.trim()}
      aria-hidden={!name}
      title={name || undefined}
    >
      {showImage ? (
        <img src={photoUrl} alt="" loading="lazy" decoding="async" onError={() => setFailed(true)} />
      ) : (
        icon || getInitials(name)
      )}
      {online !== undefined && (
        <span className={`msg-presence ${online ? 'msg-presence--online' : ''}`.trim()} />
      )}
    </span>
  );
});

/** Keeps grouped messages aligned with the avatar column. */
export function AvatarSpacer() {
  return <span className="msg-avatar-slot" aria-hidden="true" />;
}

/* ─── Badges, chips, tags ─────────────────────────────────────────────────── */

export function UnreadBadge({ count, tone = 'accent' }: { count: number; tone?: 'accent' | 'danger' }) {
  if (!count || count < 1) return null;
  return (
    <span
      className={`msg-badge ${tone === 'danger' ? 'msg-badge--danger' : ''}`.trim()}
      aria-label={`${count} unread message${count === 1 ? '' : 's'}`}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

export function MessagingTag({
  children,
  tone = 'neutral',
}: {
  children: React.ReactNode;
  tone?: 'neutral' | 'accent' | 'success' | 'warning' | 'danger';
}) {
  const toneClass = tone === 'neutral' ? '' : `msg-tag--${tone}`;
  return <span className={`msg-tag ${toneClass}`.trim()}>{children}</span>;
}

export interface FilterChipProps {
  label: string;
  count?: number;
  active?: boolean;
  onClick: () => void;
  icon?: React.ReactNode;
}

export function FilterChip({ label, count, active, onClick, icon }: FilterChipProps) {
  return (
    <button type="button" className="msg-chip" aria-pressed={!!active} onClick={onClick}>
      {icon}
      {label}
      {count !== undefined && <span className="msg-chip-count">{count}</span>}
    </button>
  );
}

/* ─── Icon button ─────────────────────────────────────────────────────────── */

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  small?: boolean;
  active?: boolean;
  accent?: boolean;
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton({ label, small, active, accent, className = '', children, ...rest }, ref) {
    return (
      <button
        ref={ref}
        type="button"
        aria-label={label}
        title={label}
        className={[
          'msg-icon-btn',
          small ? 'msg-icon-btn--sm' : '',
          active ? 'msg-icon-btn--active' : '',
          accent ? 'msg-icon-btn--accent' : '',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        {...rest}
      >
        {children}
      </button>
    );
  }
);

/* ─── Separators ──────────────────────────────────────────────────────────── */

export const DateSeparator = React.memo(function DateSeparator({ date }: { date: unknown }) {
  const label = formatDateSeparator(date);
  if (!label) return null;
  return (
    <div className="msg-date-sep" role="separator" aria-label={label}>
      <span className="msg-date-sep-line" />
      <span className="msg-date-sep-text">{label}</span>
      <span className="msg-date-sep-line" />
    </div>
  );
});

export const UnreadSeparator = React.memo(function UnreadSeparator({ count }: { count: number }) {
  return (
    <div className="msg-unread-sep" role="separator">
      <span>
        {count} new message{count === 1 ? '' : 's'}
      </span>
    </div>
  );
});

/* ─── Typing indicator ────────────────────────────────────────────────────── */

export function TypingIndicator({ names = [] }: { names?: string[] }) {
  const label =
    names.length === 0
      ? 'Typing…'
      : names.length === 1
        ? `${names[0]} is typing…`
        : `${names.length} people are typing…`;

  return (
    <div className="msg-row" aria-live="polite">
      <AvatarSpacer />
      <div className="msg-row-content">
        <div className="msg-typing" role="status" aria-label={label}>
          <span className="msg-typing-dot" />
          <span className="msg-typing-dot" />
          <span className="msg-typing-dot" />
        </div>
      </div>
    </div>
  );
}

/* ─── States ──────────────────────────────────────────────────────────────── */

export interface MessagingEmptyStateProps {
  title: string;
  description?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
}

export function MessagingEmptyState({ title, description, icon, action }: MessagingEmptyStateProps) {
  return (
    <div className="msg-state">
      <span className="msg-state-icon">{icon || <MessageSquare size={28} strokeWidth={1.75} />}</span>
      <p className="msg-state-title">{title}</p>
      {description && <p className="msg-state-text">{description}</p>}
      {action}
    </div>
  );
}

export function MessagingErrorState({
  title = 'Could not load messages',
  description = 'Check your connection and try again.',
  onRetry,
  retrying,
}: {
  title?: string;
  description?: string;
  onRetry?: () => void;
  retrying?: boolean;
}) {
  return (
    <div className="msg-state" role="alert">
      <span className="msg-state-icon msg-state-icon--danger">
        <AlertTriangle size={28} />
      </span>
      <p className="msg-state-title">{title}</p>
      <p className="msg-state-text">{description}</p>
      {onRetry && (
        <button type="button" className="msg-btn" onClick={onRetry} disabled={retrying}>
          <RefreshCw size={15} className={retrying ? 'msg-spin' : undefined} />
          {retrying ? 'Retrying…' : 'Try again'}
        </button>
      )}
    </div>
  );
}

/* ─── Skeletons ───────────────────────────────────────────────────────────── */

export function ConversationListSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="msg-skeleton-row">
          <span className="msg-skeleton" style={{ width: 40, height: 40, borderRadius: '50%' }} />
          <span style={{ flex: 1, display: 'grid', gap: 8 }}>
            <span className="msg-skeleton" style={{ height: 11, width: '55%' }} />
            <span className="msg-skeleton" style={{ height: 10, width: '80%' }} />
          </span>
        </div>
      ))}
    </div>
  );
}

export function MessageThreadSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div style={{ display: 'grid', gap: 14, padding: '8px 4px' }} aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) => {
        const own = i % 3 === 0;
        return (
          <span
            key={i}
            style={{
              display: 'flex',
              justifyContent: own ? 'flex-end' : 'flex-start',
              gap: 8,
              alignItems: 'flex-end',
            }}
          >
            {!own && (
              <span className="msg-skeleton" style={{ width: 26, height: 26, borderRadius: '50%' }} />
            )}
            <span
              className="msg-skeleton"
              style={{
                height: i % 2 ? 54 : 34,
                width: `min(${own ? 52 : 62}%, 320px)`,
                borderRadius: 18,
              }}
            />
          </span>
        );
      })}
    </div>
  );
}

/* ─── Banners ─────────────────────────────────────────────────────────────── */

export function ConnectionBanner({
  message = 'Reconnecting — showing the latest saved messages.',
  onDismiss,
}: {
  message?: string;
  onDismiss?: () => void;
}) {
  return (
    <div className="msg-banner" role="status">
      <WifiOff size={13} />
      <span style={{ flex: 1, minWidth: 0 }}>{message}</span>
      {onDismiss && (
        <IconButton label="Dismiss" small onClick={onDismiss}>
          <X size={13} />
        </IconButton>
      )}
    </div>
  );
}

export type StripTone = 'neutral' | 'accent' | 'danger' | 'warning' | 'success';

export function MessagingStrip({
  tone = 'neutral',
  icon,
  title,
  text,
  onDismiss,
  dismissLabel = 'Dismiss',
  children,
}: {
  tone?: StripTone;
  icon?: React.ReactNode;
  title?: string;
  text?: string;
  onDismiss?: () => void;
  dismissLabel?: string;
  children?: React.ReactNode;
}) {
  const toneClass = tone === 'neutral' ? '' : `msg-strip--${tone}`;
  return (
    <div className={`msg-strip ${toneClass}`.trim()} role={tone === 'danger' ? 'alert' : 'status'}>
      {icon}
      <span className="msg-strip-body">
        {title && <span className="msg-strip-title">{title}</span>}
        {text && <span className="msg-strip-text">{text}</span>}
        {children}
      </span>
      {onDismiss && (
        <IconButton label={dismissLabel} small onClick={onDismiss}>
          <X size={14} />
        </IconButton>
      )}
    </div>
  );
}

/* ─── Jump to latest ──────────────────────────────────────────────────────── */

export function JumpToLatest({
  onClick,
  label = 'Jump to latest',
}: {
  onClick: () => void;
  label?: string;
}) {
  return (
    <button type="button" className="msg-jump" onClick={onClick}>
      <ChevronDown size={14} />
      {label}
    </button>
  );
}
